import { Bell, Smartphone, Wifi } from "lucide-react";
import { SITE } from "@/config/site";
import { PlayButton } from "./PlayButton";
import { Reveal } from "./Reveal";

const POINTS = [
  {
    id: "pocket",
    icon: Smartphone,
    title: "Your clinic, in your pocket",
    text: "Schedules, patients, numbers and tasks — wherever you are, not just at the front desk.",
  },
  {
    id: "mobile-first",
    icon: Wifi,
    title: "Designed for real clinic days",
    text: "Quick to open, fast to update between patients — built around how a dental practice actually runs.",
  },
  {
    id: "alerts",
    icon: Bell,
    title: "Stay ahead, automatically",
    text: "Appointments, lab deadlines and follow-ups surface when they matter — so you don't have to remember everything.",
  },
];

const Showcase = () => (
  <section id="showcase" data-testid="showcase-section" className="py-20 sm:py-28">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
      <Reveal className="order-2 lg:order-1">
        <div className="relative mx-auto w-[280px] sm:w-[320px]" data-testid="showcase-mobile-frame">
          <div className="overflow-hidden rounded-[2.75rem] border-[10px] border-brand-ink bg-brand-deep shadow-2xl shadow-brand/30">
            <img
              src="/assets/app-today.webp"
              alt="DENSA app Today screen — appointments, collections, patients and stock alerts at a glance"
              className="aspect-[9/15] w-full object-cover object-top"
              loading="lazy"
            />
          </div>
          <div className="absolute -right-6 top-10 rounded-2xl border border-slate-100 bg-white p-2.5 shadow-xl sm:-right-10">
            <img src={SITE.assets.appIcon} alt="DENSA app icon" className="h-12 w-12 rounded-xl" />
          </div>
        </div>
      </Reveal>

      <div className="order-1 lg:order-2">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">The App Experience</p>
          <h2 data-testid="showcase-heading" className="mt-4 text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl lg:text-4xl">
            One download. <span className="text-brand">Your whole clinic.</span>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
            DENSA is an Android app built mobile-first — because dentistry doesn&apos;t happen behind a desk. This is
            the actual product you get when you download DENSA.
          </p>
        </Reveal>
        <div className="mt-8 space-y-6">
          {POINTS.map(({ id, icon: Icon, title, text }, i) => (
            <Reveal key={id} delay={0.08 * i}>
              <div className="flex gap-4" data-testid={`showcase-feature-${id}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-mint">
                  <Icon className="h-5 w-5 text-brand" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-brand-ink">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <div className="mt-9">
            <PlayButton testid="showcase-download-btn" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Showcase;
